"use client";

import "@assistant-ui/react-markdown/styles/dot.css";
import "./markdown-text.css";

import {
  type CodeHeaderProps,
  MarkdownTextPrimitive,
  unstable_memoizeMarkdownComponents as memoizeMarkdownComponents,
  useIsMarkdownCodeBlock,
} from "@assistant-ui/react-markdown";
import remarkGfm from "remark-gfm";
import { defaultUrlTransform } from "react-markdown";
import { type FC, type ReactNode, memo, useState, isValidElement } from "react";
import { usePostHog } from "posthog-js/react";

import { CheckIcon, CopyIcon } from "lucide-react";
import { trackTextCopied } from "@/utils/posthog";

import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import { cn } from "@/lib/utils";
import { InlineCitationChip } from "@/canvas/components/blocks/CitationChip";

type MarkdownTextProps = {
  hideDot?: boolean;
};

const MarkdownTextImpl = ({ hideDot }: MarkdownTextProps) => {
  return (
    <MarkdownTextPrimitive
      remarkPlugins={[remarkGfm]}
      urlTransform={(url) => (url.startsWith("ref:") ? url : defaultUrlTransform(url))}
      className={cn("aui-md", hideDot && "hide-dot")}
      components={defaultComponents}
    />
  );
};

export const MarkdownText = memo(MarkdownTextImpl);

const extractPlainText = (node: ReactNode): string => {
  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(extractPlainText).join("");
  }
  if (isValidElement(node)) {
    return extractPlainText(node.props.children);
  }
  return "";
};

const HeadingTwo: FC<React.HTMLAttributes<HTMLHeadingElement>> = ({
  className,
  children,
  ...props
}) => {
  const headingText = extractPlainText(children).trim().toLowerCase();
  if (headingText === "svar") {
    return null;
  }
  return (
    <h2
      className={cn("text-ui-title-2 mb-4 mt-8 scroll-m-20 first:mt-0 last:mb-0", className)}
      {...props}
    >
      {children}
    </h2>
  );
};

const CodeHeader: FC<CodeHeaderProps> = ({ language, code }) => {
  const posthog = usePostHog();
  const { isCopied, copyToClipboard } = useCopyToClipboard();
  const onCopy = () => {
    if (!code || isCopied) return;
    copyToClipboard(code);
    trackTextCopied(posthog, {
      source: "chat",
      text_length: code.length,
      action_source: "manual",
    });
  };

  return (
    <div className="text-ui-label mt-4 flex items-center justify-between gap-4 rounded-t-lg bg-zinc-900 px-4 py-2 text-white">
      <span className="lowercase [&>span]:text-xs">{language}</span>
      <TooltipIconButton tooltip="Copy" onClick={onCopy}>
        {!isCopied && <CopyIcon />}
        {isCopied && <CheckIcon />}
      </TooltipIconButton>
    </div>
  );
};

const useCopyToClipboard = ({
  copiedDuration = 3000,
}: {
  copiedDuration?: number;
} = {}) => {
  const [isCopied, setIsCopied] = useState<boolean>(false);

  const copyToClipboard = (value: string) => {
    if (!value) return;

    navigator.clipboard.writeText(value).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), copiedDuration);
    });
  };

  return { isCopied, copyToClipboard };
};

const defaultComponents = memoizeMarkdownComponents({
  h1: ({ className, ...props }) => (
    <h1 className={cn("text-ui-title-1 mb-8 scroll-m-20 last:mb-0", className)} {...props} />
  ),
  h2: HeadingTwo,
  h3: ({ className, ...props }) => (
    <h3
      className={cn("text-ui-title-3 mb-4 mt-6 scroll-m-20 first:mt-0 last:mb-0", className)}
      {...props}
    />
  ),
  h4: ({ className, ...props }) => (
    <h4
      className={cn("text-ui-label mb-4 mt-6 scroll-m-20 first:mt-0 last:mb-0", className)}
      {...props}
    />
  ),
  h5: ({ className, ...props }) => (
    <h5 className={cn("text-ui-label my-4 first:mt-0 last:mb-0", className)} {...props} />
  ),
  h6: ({ className, ...props }) => (
    <h6 className={cn("text-ui-label my-4 first:mt-0 last:mb-0", className)} {...props} />
  ),
  p: ({ className, ...props }) => (
    <p
      className={cn("text-ui-body fancy-p-text mb-5 mt-5 first:mt-0 last:mb-0", className)}
      {...props}
    />
  ),
  a: ({ href, children, className, ...props }) => {
    if (href?.startsWith("ref:")) {
      const withoutScheme = href.slice(4);
      const [uuidPart, hashPart] = withoutScheme.split("#");
      const sourceFileId = uuidPart || undefined;
      const parsedPage = hashPart?.startsWith("page=") ? parseInt(hashPart.slice(5), 10) : NaN;
      const pageNumber = isNaN(parsedPage) ? undefined : parsedPage;
      const fileName = extractPlainText(children as ReactNode);
      return (
        <InlineCitationChip
          fileName={fileName}
          sourceFileId={sourceFileId}
          pageNumber={pageNumber}
        />
      );
    }
    return (
      <a
        href={href}
        className={cn("text-primary font-medium underline underline-offset-4", className)}
        {...props}
      >
        {children}
      </a>
    );
  },
  blockquote: ({ className, ...props }) => (
    <blockquote className={cn("text-ui-body border-l-2 pl-6 italic", className)} {...props} />
  ),
  ul: ({ className, ...props }) => (
    <ul
      className={cn("text-ui-body fancy-p-text my-5 ml-6 list-disc [&>li]:mt-2", className)}
      {...props}
    />
  ),
  ol: ({ className, ...props }) => (
    <ol
      className={cn("text-ui-body fancy-p-text my-5 ml-6 list-decimal [&>li]:mt-2", className)}
      {...props}
    />
  ),
  hr: ({ className, ...props }) => <hr className={cn("my-5 border-b", className)} {...props} />,
  table: ({ className, ...props }) => (
    <div className="my-5 max-w-full min-w-0 overflow-x-auto">
      <table className={cn("w-full border-separate border-spacing-0", className)} {...props} />
    </div>
  ),
  th: ({ className, ...props }) => (
    <th
      className={cn(
        "text-ui-label bg-muted px-4 py-2 text-left first:rounded-tl-lg last:rounded-tr-lg [[align=center]]:text-center [[align=right]]:text-right",
        className,
      )}
      {...props}
    />
  ),
  td: ({ className, ...props }) => (
    <td
      className={cn(
        "text-ui-body-sm border-b border-l px-4 py-2 text-left last:border-r [[align=center]]:text-center [[align=right]]:text-right [&>[data-citation-chip]:only-child]:text-[0.9em] [&>p>[data-citation-chip]:only-child]:text-[0.9em]",
        className,
      )}
      {...props}
    />
  ),
  tr: ({ className, ...props }) => (
    <tr
      className={cn(
        "m-0 border-b p-0 first:border-t [&:last-child>td:first-child]:rounded-bl-lg [&:last-child>td:last-child]:rounded-br-lg",
        className,
      )}
      {...props}
    />
  ),
  sup: ({ className, ...props }) => (
    <sup className={cn("[&>a]:text-xs [&>a]:no-underline", className)} {...props} />
  ),
  pre: ({ className, ...props }) => (
    <pre
      className={cn(
        "text-ui-code overflow-x-auto rounded-t-none! rounded-b-lg bg-black p-4 text-white",
        className,
      )}
      {...props}
    />
  ),
  code: function Code({ className, ...props }) {
    const isCodeBlock = useIsMarkdownCodeBlock();
    return (
      <code
        className={cn(!isCodeBlock && "text-ui-code rounded border bg-muted", className)}
        {...props}
      />
    );
  },
  CodeHeader,
});
